import type {
  ActivityMetadata,
  AppNotification,
  NotificationAction,
  NotificationCategory,
} from "@/features/notifications/types";

/** Raw shape of a `gl_activity_log` row as returned by `xan_get_recent_activity`. */
export interface RawActivityRow {
  action: string;
  actor_source: string;
  created_at: string;
  description: string | null;
  entity_id: string;
  entity_type: string;
  id: string;
  metadata: ActivityMetadata;
  profile_id: string | null;
}

const KNOWN_ACTIONS: readonly NotificationAction[] = ["created", "updated", "deleted"];

const KNOWN_CATEGORIES: readonly NotificationCategory[] = [
  "invoice",
  "estimate",
  "payment",
  "credit",
  "expense",
  "purchase_order",
  "security",
  "system",
];

function toNotificationAction(action: string): NotificationAction {
  return (KNOWN_ACTIONS as readonly string[]).includes(action)
    ? (action as NotificationAction)
    : "updated";
}

/**
 * Maps a `gl_activity_log.entity_type` value to its display category.
 * `entity_type` is expected to already be one of the 8 categories (see
 * design.md decision 1); unrecognized values fall back to `system` so the FE
 * never crashes on a future/unexpected trigger-fed value.
 */
export function categoryForEntityType(entityType: string): NotificationCategory {
  return (KNOWN_CATEGORIES as readonly string[]).includes(entityType)
    ? (entityType as NotificationCategory)
    : "system";
}

/** Maps a raw `gl_activity_log` row to the FE's `AppNotification` shape. */
export function mapActivityRow(row: RawActivityRow): AppNotification {
  return {
    id: row.id,
    category: categoryForEntityType(row.entity_type),
    entityId: row.entity_id,
    action: toNotificationAction(row.action),
    title: row.description ?? `${row.action} ${row.entity_type} ${row.entity_id}`,
    metadata: row.metadata,
    createdAt: row.created_at,
  };
}

/**
 * A row is unread when its `created_at` is strictly after the watermark.
 * `lastReadAt = null` (never marked read) is treated as the Unix epoch, so
 * every row is unread — see design.md decision 6 for the inclusive-boundary
 * rationale.
 */
export function isActivityUnread(createdAt: string, lastReadAt: string | null): boolean {
  const watermark = lastReadAt ? new Date(lastReadAt).getTime() : 0;
  return new Date(createdAt).getTime() > watermark;
}
